﻿import GoogleAnalyticsEvent from 'dataTransferObjects/googleAnalyticsEvent';
import GoogleAnalyticsCustomDimensions from 'constants/googleAnalyticsCustomDimension';
import { createMarkerHashedKey } from 'libraries/mapLibrary';
import { interactiveSitePlanSvg } from 'constants/svg-assets';
import { callForPriceHomeSvgElement, priceReducedSvgElement } from '../constants/svgIcons';
import MarkerType from '../enums/markerType';
import { isPlanViewed } from '../services/markerSessionService';
import {
  callForPriceHomeActiveSvgElement,
  markerArrowHoverSvgElement,
  markerArrowSvgElement,
} from '../constants/svgIcons.b1';
import getClassesToApply from '../services/unitMarkerService';

/* Creates a new instance of a housing unit that will be plotted in the map as a marker. */
export default class PlanUnitMarker {
  constructor(housingInformation) {
    const totalPlans = housingInformation.TotalPlans ?? 1;
    this.geoId = housingInformation.GeoId;
    this.detailText = housingInformation.DetailText;
    this.type = MarkerType.PlanUnit.toString();
    this.id = housingInformation.Id;
    this.communityId = housingInformation.CommunityId;
    this.isBasic = housingInformation.IsBasic || housingInformation.IsBl;
    this.latitude = housingInformation.Latitude;
    this.longitude = housingInformation.Longitude;
    // TODO: NewHomeSource Rebrand
    // Change the class on the backend.
    this.priceText = housingInformation.PriceText.replace('small--text', 'nhs-c-tooltip__price-label--small');
    this.shortPrice = housingInformation.ShortPrice;
    this.title = housingInformation.Title;
    this.totalPlans = +totalPlans;
    this.priceReducedText = housingInformation.PriceReducedText;
    this.isPriceReduction = !!housingInformation.PriceReducedText;
    this.previousPriceText = housingInformation.PreviousPriceText;
    this.priceReducedTrackingLabel = housingInformation.PriceReducedTrackingLabel;
    this.isFeaturedListing = housingInformation.IsFeaturedListing;
    this.isCallForPrice = housingInformation.IsCallForPrice;
    this.isViewed = isPlanViewed(housingInformation.Id);
    this.price = housingInformation.Price;
    this.hasInteractiveSitePlan = housingInformation.HasInteractiveSitePlan;
    this.sitePlanCenterlatitude = housingInformation.SitePlanCenterLatitude;
    this.sitePlanCenterlongitude = housingInformation.SitePlanCenterLongitude;

    if (this.isBasic) {
      this.zIndex = 1;
    } else if (this.isFeaturedListing) {
      this.zIndex = 4;
    } else {
      this.zIndex = 2;
    }
  }

  /**
   * getMarkerText: gets the text the marker is going to show once rendered.
   * @returns a text to show on the map once rendered.
   */
  getMarkerText() {
    return this.shortPrice;
  }

  /**
   * getMarkerTextNode: gets the html node to represent the marker text
   * @returns a string representing the html node to render a marker text
   */
  getMarkerTextNode() {
    if (this.shortPrice && !this.isCallForPrice) {
      return `<div data-marker-text class="nhs-c-marker__text" data-qa="marker_price">
                ${this.shortPrice}
              </div>`;
    }

    return '';
  }

  /**
   * @returns The name of the extend marker type.
   */
  getType() {
    return this.type;
  }

  /**
   * getInteractiveSitePlanSection: adds a <p> with the information needed to reach
   * the interactive site plan of the marker, if any.
   */
  getInteractiveSitePlanSection() {
    if (!this.hasInteractiveSitePlan) {
      return '';
    }

    return `<button
              class="nhs-c-button--text-only nhs-c-button--primary-ui nhs-c-button--small-32 nhs-u-flex-row nhs-u-gap--200"
              data-interactive-plan-button
              data-latitude="${this.sitePlanCenterlatitude}"
              data-longitude="${this.sitePlanCenterlongitude}"
              data-community-id="${this.communityId}">
              <span class='nhs-c-icon--16'>${interactiveSitePlanSvg}</span> Interactive Map
          </button>`;
  }

  /**
   * @returns The HTML for the card element to be associated to this housing unit marker.
   */
  getCardElementDefault() {
    const hoverCard = document.createElement('div');
    hoverCard.classList.add('nhs-c-tooltip--map');
    hoverCard.dataset.id = this.id;
    hoverCard.dataset.tooltip = true;
    hoverCard.dataset.markerStyles = this.getMarkerStyles();

    hoverCard.innerHTML = `
    <div class="nhs-c-tooltip__title-container">${this.shortPrice || this.priceText}</div>
    ${this.getFacts()}
    ${this.getInteractiveSitePlanSection()}
    <div hidden data-marker-clone>${this.getHtmlPinElement().innerHTML}</div>`;
    return hoverCard;
  }

  /**
   * @returns The HTML for the card element to be associated to this housing unit marker. This card has
   * the peculiarity to show price reduction information.
   */
  getCardElementWithPriceReduction() {
    const hoverCard = document.createElement('div');
    hoverCard.classList.add('nhs-c-tooltip--map');
    hoverCard.dataset.id = this.id;
    hoverCard.dataset.tooltip = true;
    hoverCard.dataset.markerStyles = this.getMarkerStyles();

    hoverCard.innerHTML = `
    <div class="nhs-c-tooltip__title-container">
      ${this.shortPrice || this.priceText}
      <span class="nhs-c-tooltip__price-label--small-strikethrough" data-qa="strike-thru-price>
        ${this.previousPriceText}
      </span>
    </div>
    <p class="nhs-c-tooltip__price-reduction" data-qa="price-reduction">
     <span class='nhs-c-tooltip__price-reduction-icon'>${priceReducedSvgElement.cloneNode(true).outerHTML}</span>
     ${this.priceReducedText}
    </p>
    ${this.getFacts()}
    ${this.getInteractiveSitePlanSection()}
    <div hidden data-marker-clone>${this.getHtmlPinElement().innerHTML}</div>`;
    return hoverCard;
  }

  getFacts() {
    if (this.isCallForPrice) {
      return '';
    }

    return `<p class='nhs-c-tooltip__facts'>${this.detailText}</p>`;
  }

  /**
   * It returns a string that is the url to the detail page of the home plan
   * @returns The string `/homedetail/planid-${this.id}`
   */
  getDetailUrl() {
    return `/homedetail/planid-${this.id}`;
  }

  /**
   * This function returns a new GoogleAnalyticsEvent object with the event category, event action, and
   * event label set.
   * @returns A new instance of the GoogleAnalyticsEvent class.
   */
  getGoogleAnalyticsEventForClicks() {
    const customDimensions = {
      [GoogleAnalyticsCustomDimensions.PriceReduction().getName()]: this.priceReducedTrackingLabel,
      [GoogleAnalyticsCustomDimensions.PlanId().getName()]: this.id,
      [GoogleAnalyticsCustomDimensions.IsPaid().getName()]: !this.isBasic,
    };
    const url = this.getDetailUrl();
    return new GoogleAnalyticsEvent('Map-Pin-Clicks', 'Homes: SRP-Map-Home-Clicks', url, customDimensions);
  }

  /**
   * This function returns a new meta data object with the required information based on the map pin data.
   * @returns A new instance of the meta data object.
   */
  getHousingLocationMetaData() {
    return {
      priceReducedTrackingLabel: this.priceReducedTrackingLabel,
      planId: this.id,
      isPaid: !this.isBasic,
      priceLow: this.price,
      hasInteractiveSitePlan: this.hasInteractiveSitePlan,
      communityId: this.communityId,
    };
  }

  /**
   * This function returns the HTML in charge of build the hover card of a given marker
   * @returns hover card HTML
   */
  getCard() {
    if (this.priceReducedText) {
      return this.getCardElementWithPriceReduction();
    }

    return this.getCardElementDefault();
  }

  /**
   * @returns The complete HTML that represent the housing unit marker as an icon and a card with details.
   */
  getIcon() {
    const parentElement = document.createElement('div');
    const htmlPinElement = this.getHtmlPinElement();
    const cardElement = this.getCard();

    parentElement.appendChild(htmlPinElement);
    parentElement.appendChild(cardElement);
    parentElement.classList.add('nhs-c-advance-marker', 'nhs-c-tooltip-hide', 'nhs-mq-tooltip-show--large');

    return parentElement;
  }

  /**
   * getMarkerIcon: gets the html in charge of representing the marker icon given some important attribute of it,
   * like being a price reduction marker, call for price marker, among others.
   * @returns a html string representing the marker icon.
   */
  getMarkerIcon() {
    if (this.isCallForPrice) {
      return `
      <div class="nhs-c-marker__icon-no-price">
        <div class="nhs-c-marker__icon--default">${callForPriceHomeSvgElement.cloneNode(true).outerHTML}</div>
        <div class="nhs-c-marker__icon--hover">${callForPriceHomeActiveSvgElement.cloneNode(true).outerHTML}</div>
      </div>`;
    }

    if (this.priceReducedText) {
      return `<div class="nhs-c-marker__icon">${priceReducedSvgElement.cloneNode(true).outerHTML}</div>`;
    }

    return '';
  }

  /**
   * getDescription: returns a description that will help screen readers
   * provide a better explanation about the marker itself.
   * @returns a description for the marker.
   */
  getDescription() {
    if (this.shortPrice) {
      return `Plan from ${this.shortPrice}`;
    }

    return this.title;
  }

  /**
   * getMarkerStyles function is in charge of returning an array with the classes to use for the map marker.
   * @returns an array representing all the classes we should apply to the map marker in terms of its characteristics.
   */
  getMarkerStyles() {
    const { base, viewed } = getClassesToApply({
      isBasic: this.isBasic,
      isNoPrice: this.isCallForPrice,
      isSpotlight: this.isFeaturedListing,
      isViewed: this.isViewed,
      isPriceReduction: this.isPriceReduction,
    });

    const classesToApply = [...base, ...viewed].filter(Boolean);
    return classesToApply;
  }

  /**
   * setMarkerInformation: this callback takes the marker node and sets the data attributes needed to
   * identifying the marker and render it in the right map spot.
   * @param {HTMLDivElement} marker that will hold the information this callback sets.
   */
  setMarkerInformation(marker) {
    marker.dataset.id = this.id;
    marker.dataset.type = this.getType();
    marker.dataset.geographicKey = createMarkerHashedKey(this.latitude, this.longitude);
    marker.dataset.priceReducedTrackingLabel = this.priceReducedTrackingLabel;
    marker.dataset.isSpotlight = this.isFeaturedListing;
    marker.dataset.isNoPrice = this.isCallForPrice;
    marker.dataset.viewed = this.isViewed;
    marker.dataset.isPriceReduction = this.isPriceReduction;
    marker.dataset.qa = 'marker_container';

    if (this.isBasic) {
      marker.dataset.isBasic = true;
    } else {
      marker.dataset.isPaid = true;
    }
  }

  /**
   * Creates a HTML element representing a pin marker of plan unit.
   * @returns the HTML Element that can represent the marker in the map.
   */
  getHtmlPinElement() {
    const element = document.createElement('div');
    element.dataset.markerIcon = true;
    element.classList.add(...this.getMarkerStyles());
    this.setMarkerInformation(element);

    if (this.isBasic) {
      return element;
    }

    const arrow = markerArrowSvgElement.cloneNode(true);
    arrow.classList.add('nhs-c-marker__arrow--default');

    const hoverArrow = markerArrowHoverSvgElement.cloneNode(true);
    hoverArrow.classList.add('nhs-c-marker__arrow--hover');

    element.innerHTML = `
        <div class="nhs-c-marker__box">
          ${this.getMarkerIcon()}
          ${this.getMarkerTextNode()}
          ${arrow.outerHTML}
          ${hoverArrow.outerHTML}
        </div>
      `;

    return element;
  }
}
